import React from 'react';
import {
  Box, Button, Checkbox, Chip, FormControl, LinearProgress, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Tooltip, Typography
} from '@mui/material';
import { fmtMoney } from '../../Documents/format';
import { fmtInt, fmtQty } from '../../OnlineJournal/format';
import { Option, ProductRow, ProductSort, ProductSortKey } from '../remainsCalc';
import { BORDER, headCellSx, panelSx, rowSx, smallChipSx, tableContainerSx } from '../remainsStyles';
import { ExpiryCell, Value } from './RemainsCommon';

interface Props {
  rows: ProductRow[];
  /** Всего найдено товаров (до фильтра по папке) */
  total: number;
  folders: Option[];
  /** null - все папки, '' - без папки */
  folder: string | null;
  sort: ProductSort;
  selected: string[];
  loading: boolean;
  withSums: boolean;
  withKm: boolean;
  onFolder: (folder: string | null) => void;
  onSort: (key: ProductSortKey) => void;
  onToggle: (zid: string) => void;
  onSelect: (zids: string[]) => void;
  onClear: () => void;
}

interface Column {
  id: string;
  label: string;
  width: number;
  align?: 'right' | 'center';
  sort?: ProductSortKey;
  tip?: string;
}

const ALL_FOLDERS = '*';
const NO_FOLDER = '-';

function columnsFor(withSums: boolean, withKm: boolean): Column[] {
  return [
    { id: 'name', label: 'Товар', width: 300, sort: 'name' },
    { id: 'producer', label: 'Производитель', width: 160 },
    { id: 'mnn', label: 'МНН', width: 170 },
    { id: 'category', label: 'Категория', width: 140 },
    { id: 'folder', label: 'Папка / тип 1С', width: 170, tip: 'Группа номенклатуры (папка) и вид товара в 1С' },
    { id: 'part', label: 'Делимость', width: 90, align: 'center', tip: 'Сколько частей в упаковке, если её можно продавать поштучно' },
    { id: 'pharmacies', label: 'Аптек', width: 80, align: 'right', sort: 'pharmacies' },
    { id: 'units', label: 'Кол-во', width: 90, align: 'right', sort: 'units' },
    ...(withSums ? [{ id: 'amount', label: 'Сумма', width: 120, align: 'right' as const, sort: 'amount' as const }] : []),
    { id: 'expiry', label: 'Ближ. срок', width: 120, sort: 'expiry', tip: 'По журналу сроков, без серий' },
    ...(withKm ? [
      { id: 'manager', label: 'Менеджер КМ', width: 150 },
      { id: 'priceGroup', label: 'Цен. группа', width: 120 },
      { id: 'bans', label: 'Запреты', width: 130, tip: 'Отметки 1С: не участвует в перемещениях / возвратах' }
    ] : [])
  ];
}

const banChipSx = { ...smallChipSx, height: 20, fontSize: 10.5, color: '#b45309', bgcolor: 'rgba(245,158,11,0.1)' };

function renderCell(id: string, r: ProductRow): React.ReactNode {
  const p = r.product;
  switch (id) {
    case 'name': {
      const ids = [p.sku ? `арт. ${p.sku}` : '', p.barcode ? `ШК ${p.barcode}` : ''].filter(Boolean).join(' · ');
      return (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            title={p.name}
            sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {p.name}
          </Typography>
          {ids && <Typography sx={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }} noWrap title={ids}>{ids}</Typography>}
        </Box>
      );
    }
    case 'producer': return <Value text={p.producer} />;
    case 'mnn': return <Value text={p.mnn} />;
    case 'category': return <Value text={p.category} />;
    case 'folder':
      return (
        <Box sx={{ minWidth: 0 }}>
          <Value text={p.folder} />
          {p.type && <Typography sx={{ fontSize: 11, color: '#94a3b8' }} noWrap title={p.type}>{p.type}</Typography>}
        </Box>
      );
    case 'part':
      return p.partCoeff ? <Chip size="small" label={`1/${p.partCoeff}`} sx={{ ...smallChipSx, color: '#0f766e', bgcolor: 'rgba(13,148,136,0.08)' }} /> : <Typography sx={{ fontSize: 12.5, color: '#cbd5e1' }}>нет</Typography>;
    case 'pharmacies':
      return r.pharmacies ? fmtInt(r.pharmacies) : <Typography component="span" sx={{ fontSize: 12.5, color: '#94a3b8' }}>0</Typography>;
    case 'units':
      return <Typography sx={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtQty(r.units)}</Typography>;
    case 'amount':
      return <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtMoney(r.amount)}</Typography>;
    case 'expiry':
      return <ExpiryCell nearest={r.nearest} expiredQty={r.expiredQty} />;
    case 'manager': return <Value text={p.manager} />;
    case 'priceGroup': return <Value text={p.priceGroup} />;
    case 'bans':
      if (!p.notForMovements && !p.notForReturns) return <Typography sx={{ fontSize: 12.5, color: '#cbd5e1' }}>нет</Typography>;
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {p.notForMovements && <Chip size="small" label="перемещ." sx={banChipSx} />}
          {p.notForReturns && <Chip size="small" label="возврат" sx={banChipSx} />}
        </Box>
      );
    default:
      return null;
  }
}

/** Найденные товары: выбор строк ограничивает таблицу аптек, карту и выгрузку */
const ProductsPanel: React.FC<Props> = ({
  rows, total, folders, folder, sort, selected, loading, withSums, withKm, onFolder, onSort, onToggle, onSelect, onClear
}) => {
  const columns = columnsFor(withSums, withKm);
  const picked = new Set(selected);
  const shownZids = rows.map(r => r.product.zid);
  const shownPicked = shownZids.filter(z => picked.has(z)).length;
  const allShown = shownZids.length > 0 && shownPicked === shownZids.length;
  // выбор в других папках сохраняется: показанные добавляются к нему или убираются из него
  const addShown = () => onSelect(selected.concat(shownZids.filter(z => !picked.has(z))));
  const removeShown = () => onSelect(selected.filter(z => shownZids.indexOf(z) === -1));
  const minWidth = columns.reduce((acc, c) => acc + c.width, 48);
  const folderValue = folder === null ? ALL_FOLDERS : folder === '' ? NO_FOLDER : folder;

  return (
    <Paper variant="outlined" sx={panelSx}>
      <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
      <Box sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
          Товаров <Box component="b" sx={{ color: '#0f172a' }}>{fmtInt(rows.length)}</Box>
          {rows.length !== total && <> из {fmtInt(total)}</>}
          {selected.length > 0 && <> · выбрано <Box component="b" sx={{ color: '#1e40af' }}>{fmtInt(selected.length)}</Box></>}
        </Typography>
        {folders.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={folderValue} sx={{ height: 32, fontSize: '0.82rem' }}
              onChange={e => {
                const v = String(e.target.value);
                onFolder(v === ALL_FOLDERS ? null : v === NO_FOLDER ? '' : v);
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
            >
              <MenuItem value={ALL_FOLDERS}>Все папки 1С</MenuItem>
              {folders.map(f => (
                <MenuItem key={f.value || NO_FOLDER} value={f.value || NO_FOLDER} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography sx={{ fontSize: '0.85rem' }}>{f.label}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{f.count}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={addShown} disabled={!rows.length || allShown} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Выбрать показанные
        </Button>
        <Button size="small" color="inherit" onClick={onClear} disabled={!selected.length} sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
          Снять выбор
        </Button>
      </Box>

      <TableContainer sx={tableContainerSx}>
        <Table size="small" stickyHeader sx={{ minWidth, tableLayout: 'fixed', opacity: loading && rows.length ? 0.6 : 1, transition: 'opacity .15s' }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ ...headCellSx, width: 48 }}>
                <Checkbox
                  size="small" checked={allShown} indeterminate={shownPicked > 0 && !allShown} disabled={!rows.length}
                  onChange={() => (allShown ? removeShown() : addShown())}
                  inputProps={{ 'aria-label': 'Выбрать все показанные товары' }}
                />
              </TableCell>
              {columns.map(col => {
                const label = col.sort ? (
                  <TableSortLabel active={sort.key === col.sort} direction={sort.key === col.sort ? sort.dir : 'asc'} onClick={() => onSort(col.sort as ProductSortKey)}>
                    {col.label}
                  </TableSortLabel>
                ) : col.label;
                return (
                  <TableCell key={col.id} align={col.align} sx={{ ...headCellSx, width: col.width }}>
                    {col.tip ? <Tooltip title={col.tip} arrow><span>{label}</span></Tooltip> : label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => {
              const on = picked.has(r.product.zid);
              return (
                <TableRow
                  key={r.product.zid} hover selected={on} onClick={() => onToggle(r.product.zid)}
                  sx={{ ...rowSx, cursor: 'pointer', opacity: r.pharmacies ? 1 : 0.6 }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small" checked={on} onClick={e => e.stopPropagation()} onChange={() => onToggle(r.product.zid)}
                      inputProps={{ 'aria-label': `Выбрать ${r.product.name}` }}
                    />
                  </TableCell>
                  {columns.map(col => (
                    <TableCell key={col.id} align={col.align} sx={{ overflow: 'hidden' }}>{renderCell(col.id, r)}</TableCell>
                  ))}
                </TableRow>
              );
            })}

            {!rows.length && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 6, color: '#64748b' }}>
                  <Typography variant="body2">{total ? 'В этой папке найденных товаров нет' : 'Товары не найдены'}</Typography>
                  {total > 0 && (
                    <Typography
                      variant="body2" onClick={() => onFolder(null)}
                      sx={{ mt: 0.5, color: 'primary.main', cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Показать все папки
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default React.memo(ProductsPanel);
